import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition, Tab } from '@headlessui/react';
import {
  PlusIcon,
  MegaphoneIcon,
  CheckCircleIcon,
  ChartBarIcon,
  ClockIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { campaignApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import FeatureGate from '../components/FeatureGate';
import PermissionGate from '../components/PermissionGate';
import { PageHeader, StatusBadge, StatCard } from '../components/ui';
import type { BadgeVariant } from '../components/ui';

// ─── Types ────────────────────────────────────────────────────────────────────

type CampaignStatus = 'PLANNING' | 'IN_REVIEW' | 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';

type ContentChannelType =
  | 'PRINT'
  | 'WEB'
  | 'SOCIAL'
  | 'EMAIL'
  | 'RADIO'
  | 'TV'
  | 'DIRECT_MAIL'
  | 'IN_BRANCH'
  | 'MOBILE';

interface Campaign {
  id: string;
  name: string;
  description?: string;
  status: CampaignStatus;
  channels: ContentChannelType[];
  startDate: string;
  endDate: string;
  piecesCount: number;
  complianceStatus: 'compliant' | 'pending_review' | 'has_issues' | null;
  targetProductTypes?: string[];
}

interface CampaignDashboard {
  totalCampaigns: number;
  activeCampaigns: number;
  complianceRate: number;
  pendingReview: number;
}

interface CreateCampaignForm {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  channels: ContentChannelType[];
  targetProductTypes: string[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_VARIANT: Record<CampaignStatus, BadgeVariant> = {
  PLANNING: 'draft',
  IN_REVIEW: 'info',
  ACTIVE: 'success',
  PAUSED: 'warning',
  COMPLETED: 'neutral',
  CANCELLED: 'danger',
};

const STATUS_LABEL: Record<CampaignStatus, string> = {
  PLANNING: 'Planning',
  IN_REVIEW: 'In Review',
  ACTIVE: 'Active',
  PAUSED: 'Paused',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

const COMPLIANCE_VARIANT: Record<string, BadgeVariant> = {
  compliant: 'success',
  pending_review: 'warning',
  has_issues: 'danger',
};

const COMPLIANCE_LABEL: Record<string, string> = {
  compliant: 'Compliant',
  pending_review: 'Pending Review',
  has_issues: 'Has Issues',
};

const CHANNEL_DISPLAY: Record<ContentChannelType, string> = {
  PRINT: 'Print',
  WEB: 'Web',
  SOCIAL: 'Social',
  EMAIL: 'Email',
  RADIO: 'Radio',
  TV: 'TV',
  DIRECT_MAIL: 'Direct Mail',
  IN_BRANCH: 'In-Branch',
  MOBILE: 'Mobile',
};

const ALL_CHANNELS: ContentChannelType[] = [
  'PRINT',
  'WEB',
  'SOCIAL',
  'EMAIL',
  'RADIO',
  'TV',
  'DIRECT_MAIL',
  'IN_BRANCH',
  'MOBILE',
];

const PRODUCT_TYPES = [
  'Mortgage',
  'Auto Loan',
  'Credit Card',
  'Personal Loan',
  'Savings Account',
  'Checking Account',
  'CD',
  'Home Equity',
];

const TABS = ['All', 'Planning', 'In Review', 'Active', 'Completed', 'Cancelled'] as const;

const TAB_STATUS_MAP: Record<string, CampaignStatus | undefined> = {
  All: undefined,
  Planning: 'PLANNING',
  'In Review': 'IN_REVIEW',
  Active: 'ACTIVE',
  Completed: 'COMPLETED',
  Cancelled: 'CANCELLED',
};

const DEFAULT_FORM: CreateCampaignForm = {
  name: '',
  description: '',
  startDate: '',
  endDate: '',
  channels: [],
  targetProductTypes: [],
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function CampaignManagementPage() {
  const queryClient = useQueryClient();
  const { teamId } = useTeamId();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [page, setPage] = useState(1);
  const [formData, setFormData] = useState<CreateCampaignForm>(DEFAULT_FORM);
  const limit = 20;

  const activeStatus = TAB_STATUS_MAP[TABS[selectedTab]];

  // ── Queries ──────────────────────────────────────────────────────────────

  const { data: dashboardData } = useQuery({
    queryKey: ['campaigns', 'dashboard', teamId],
    queryFn: () => campaignApi.dashboard(teamId!),
    enabled: !!teamId,
  });
  const dashboard: CampaignDashboard | undefined = dashboardData?.data;

  const { data: campaignsData, isLoading } = useQuery({
    queryKey: ['campaigns', teamId, activeStatus, page, limit],
    queryFn: () =>
      campaignApi.list(teamId!, {
        status: activeStatus,
        page,
        limit,
      }),
    enabled: !!teamId,
  });
  const campaigns: Campaign[] = campaignsData?.data?.data || [];
  const totalCount: number = campaignsData?.data?.pagination?.total || 0;
  const totalPages = Math.ceil(totalCount / limit);

  // ── Mutations ────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (data: CreateCampaignForm) => campaignApi.create(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Campaign created successfully');
      setShowCreateModal(false);
      setFormData(DEFAULT_FORM);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to create campaign');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => campaignApi.delete(teamId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Campaign deleted');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to delete campaign');
    },
  });

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleCreate = () => {
    if (!formData.name.trim()) {
      toast.error('Campaign name is required');
      return;
    }
    if (!formData.startDate || !formData.endDate) {
      toast.error('Start and end dates are required');
      return;
    }
    if (formData.channels.length === 0) {
      toast.error('Select at least one channel');
      return;
    }
    createMutation.mutate(formData);
  };

  const toggleChannel = (channel: ContentChannelType) => {
    setFormData((prev) => ({
      ...prev,
      channels: prev.channels.includes(channel)
        ? prev.channels.filter((c) => c !== channel)
        : [...prev.channels, channel],
    }));
  };

  const toggleProductType = (type: string) => {
    setFormData((prev) => ({
      ...prev,
      targetProductTypes: prev.targetProductTypes.includes(type)
        ? prev.targetProductTypes.filter((t) => t !== type)
        : [...prev.targetProductTypes, type],
    }));
  };

  const handleDelete = (id: string, name: string) => {
    if (window.confirm(`Delete campaign "${name}"? This action cannot be undone.`)) {
      deleteMutation.mutate(id);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <FeatureGate feature="multi_channel_governance">
      <div className="space-y-6">
        {/* Header */}
        <PageHeader
          title="Campaign Management"
          description="Create and manage multi-channel marketing campaigns with compliance tracking."
          actions={
            <PermissionGate teamId={teamId} permission="compliance.write">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 transition-colors"
              >
                <PlusIcon className="h-4 w-4" />
                New Campaign
              </button>
            </PermissionGate>
          }
        />

        {/* Stats Row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={MegaphoneIcon}
            label="Total Campaigns"
            value={dashboard?.totalCampaigns ?? 0}
          />
          <StatCard
            icon={CheckCircleIcon}
            label="Active Campaigns"
            value={dashboard?.activeCampaigns ?? 0}
          />
          <StatCard
            icon={ChartBarIcon}
            label="Compliance Rate"
            value={dashboard?.complianceRate != null ? `${dashboard.complianceRate}%` : '--'}
          />
          <StatCard
            icon={ClockIcon}
            label="Pending Review"
            value={dashboard?.pendingReview ?? 0}
          />
        </div>

        {/* Tab Bar */}
        <Tab.Group
          selectedIndex={selectedTab}
          onChange={(index) => {
            setSelectedTab(index);
            setPage(1);
          }}
        >
          <Tab.List className="flex space-x-1 border-b border-gray-200">
            {TABS.map((tab) => (
              <Tab
                key={tab}
                className={({ selected }) =>
                  clsx(
                    'whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors focus:outline-none',
                    selected
                      ? 'border-teal-600 text-teal-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                  )
                }
              >
                {tab}
              </Tab>
            ))}
          </Tab.List>

          <Tab.Panels className="mt-4">
            {TABS.map((tab) => (
              <Tab.Panel key={tab}>
                {isLoading ? (
                  <div className="flex items-center justify-center rounded-lg border border-gray-100 bg-white p-12 shadow-sm">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-teal-600 border-t-transparent" />
                  </div>
                ) : campaigns.length === 0 ? (
                  <div className="rounded-lg border border-gray-100 bg-white p-12 text-center shadow-sm">
                    <MegaphoneIcon className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-3 text-sm font-medium text-gray-900">No campaigns found</p>
                    <p className="mt-1 text-xs text-gray-500">
                      Create your first campaign to get started with multi-channel governance.
                    </p>
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="mt-4 inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500"
                    >
                      <PlusIcon className="h-4 w-4" />
                      New Campaign
                    </button>
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-lg border border-gray-100 bg-white shadow-sm">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200 bg-gray-50">
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                              Name
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                              Status
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                              Channels
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                              Date Range
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                              Pieces
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                              Compliance
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {campaigns.map((campaign) => (
                            <tr key={campaign.id} className="transition-colors hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm">
                                <Link
                                  to={`/campaigns/${campaign.id}`}
                                  className="font-medium text-teal-600 hover:text-teal-700 hover:underline"
                                >
                                  {campaign.name}
                                </Link>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <StatusBadge variant={STATUS_VARIANT[campaign.status]}>
                                  {STATUS_LABEL[campaign.status]}
                                </StatusBadge>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <div className="flex flex-wrap gap-1">
                                  {campaign.channels.map((ch) => (
                                    <StatusBadge key={ch} variant="neutral">
                                      {CHANNEL_DISPLAY[ch]}
                                    </StatusBadge>
                                  ))}
                                </div>
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                                {new Date(campaign.startDate).toLocaleDateString()} &ndash;{' '}
                                {new Date(campaign.endDate).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700 tabular-nums">
                                {campaign.piecesCount}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {campaign.complianceStatus ? (
                                  <StatusBadge
                                    variant={COMPLIANCE_VARIANT[campaign.complianceStatus] || 'neutral'}
                                  >
                                    {COMPLIANCE_LABEL[campaign.complianceStatus] || campaign.complianceStatus}
                                  </StatusBadge>
                                ) : (
                                  <span className="text-xs text-gray-400">--</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <div className="flex items-center gap-2">
                                  <Link
                                    to={`/campaigns/${campaign.id}`}
                                    className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-teal-600 hover:bg-teal-50 transition-colors"
                                  >
                                    <PencilSquareIcon className="h-3.5 w-3.5" />
                                    Edit
                                  </Link>
                                  <button
                                    onClick={() => handleDelete(campaign.id, campaign.name)}
                                    className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
                                  >
                                    <TrashIcon className="h-3.5 w-3.5" />
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-4 py-3">
                        <p className="text-xs text-gray-500">
                          Page {page} of {totalPages} &middot; {totalCount} total
                        </p>
                        <div className="flex gap-1">
                          <button
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page <= 1}
                            className="rounded border border-gray-300 px-3 py-1 text-xs font-medium transition-colors hover:bg-white disabled:opacity-40"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages}
                            className="rounded border border-gray-300 px-3 py-1 text-xs font-medium transition-colors hover:bg-white disabled:opacity-40"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Tab.Panel>
            ))}
          </Tab.Panels>
        </Tab.Group>

        {/* ── Create Campaign Modal ──────────────────────────────────────────── */}
        <Transition appear show={showCreateModal} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setShowCreateModal(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
                    <Dialog.Title className="text-lg font-semibold text-navy-900">
                      New Campaign
                    </Dialog.Title>

                    <div className="mt-4 space-y-4">
                      {/* Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Campaign Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="e.g., Q1 Mortgage Promotion"
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      </div>

                      {/* Description */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          rows={3}
                          placeholder="Brief description of the campaign objectives and scope"
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      </div>

                      {/* Date Range */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Start Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={formData.startDate}
                            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            End Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={formData.endDate}
                            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                          />
                        </div>
                      </div>

                      {/* Channels (multi-select checkboxes) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Channels <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-2 grid grid-cols-3 gap-2">
                          {ALL_CHANNELS.map((channel) => (
                            <label
                              key={channel}
                              className={clsx(
                                'flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors',
                                formData.channels.includes(channel)
                                  ? 'border-teal-500 bg-teal-50 text-teal-700'
                                  : 'border-gray-200 text-gray-700 hover:border-gray-300',
                              )}
                            >
                              <input
                                type="checkbox"
                                checked={formData.channels.includes(channel)}
                                onChange={() => toggleChannel(channel)}
                                className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                              />
                              {CHANNEL_DISPLAY[channel]}
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Target Product Types (optional multi-select) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Target Product Types{' '}
                          <span className="text-xs font-normal text-gray-400">(optional)</span>
                        </label>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {PRODUCT_TYPES.map((type) => (
                            <button
                              key={type}
                              type="button"
                              onClick={() => toggleProductType(type)}
                              className={clsx(
                                'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                                formData.targetProductTypes.includes(type)
                                  ? 'border-teal-500 bg-teal-50 text-teal-700'
                                  : 'border-gray-200 text-gray-600 hover:border-gray-300',
                              )}
                            >
                              {type}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        onClick={() => {
                          setShowCreateModal(false);
                          setFormData(DEFAULT_FORM);
                        }}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreate}
                        disabled={
                          !formData.name.trim() ||
                          !formData.startDate ||
                          !formData.endDate ||
                          formData.channels.length === 0 ||
                          createMutation.isPending
                        }
                        className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50 transition-colors"
                      >
                        {createMutation.isPending ? 'Creating...' : 'Create Campaign'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </FeatureGate>
  );
}
